import { useState, useEffect } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { 
  Users, 
  ArrowRight, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  RefreshCw,
  DollarSign,
  UserCheck
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMigrateVendors = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/referrals/admin/migration-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      toast.error("Error al cargar preview de migración");
    } finally {
      setLoading(false);
    }
  };

  const executeMigration = async () => {
    if (!window.confirm(
      `¿Estás seguro de migrar ${preview?.total_vendors || 0} vendedores a VIP Partners?\n\n` +
      `Esta acción:\n` +
      `• Cambiará su rol de "vendedor" a "cliente"\n` +
      `• Los marcará como VIP Partners\n` +
      `• Generará códigos de referido\n` +
      `• Migrará comisiones pendientes\n\n` +
      `Esta acción NO se puede deshacer.`
    )) {
      return;
    }

    setMigrating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/referrals/admin/migrate-vendors`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMigrationResult(response.data);
      toast.success(`¡Migración completada! ${response.data.migrated} vendedores migrados`);
      fetchPreview(); // Refresh preview
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la migración");
    } finally {
      setMigrating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Migrar Vendedores a VIP Partners</h1>
          <p className="text-gray-500 mt-1">Convierte el sistema de vendedores al nuevo sistema simplificado</p>
        </div>

        {/* Migration Result */}
        {migrationResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-xl font-semibold text-emerald-800">¡Migración Completada!</h2>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-emerald-600">{migrationResult.migrated}</p>
                <p className="text-sm text-gray-600">Vendedores migrados</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{migrationResult.codes_created}</p>
                <p className="text-sm text-gray-600">Códigos creados</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{migrationResult.commissions_migrated}</p>
                <p className="text-sm text-gray-600">Comisiones migradas</p>
              </div>
            </div>
            {migrationResult.details && migrationResult.details.length > 0 && (
              <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
                <h3 className="font-medium text-gray-900 mb-2">Detalles:</h3>
                {migrationResult.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-900">{detail.name}</p>
                      <p className="text-sm text-gray-500">{detail.email}</p>
                    </div>
                    <div className="text-right">
                      <code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-sm">{detail.new_code}</code>
                      {detail.commissions_migrated > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{detail.commissions_migrated} comisiones</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preview Card */}
        {!migrationResult && preview && (
          <>
            {preview.total_vendors === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No hay vendedores para migrar</h2>
                <p className="text-gray-600">
                  Todos los vendedores ya han sido migrados o no existen usuarios con rol "vendedor" en el sistema.
                </p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Vendedores a migrar</p>
                        <p className="text-2xl font-bold text-blue-600">{preview.total_vendors}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Comisiones pendientes</p>
                        <p className="text-2xl font-bold text-amber-600">{preview.total_pending_commissions}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Monto pendiente</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(preview.total_pending_amount)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Migration Flow */}
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-4">¿Qué hará la migración?</h3>
                  <div className="flex items-center justify-center gap-4">
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Vendedor</p>
                      <p className="text-xs text-gray-500">role: "vendedor"</p>
                    </div>
                    <ArrowRight className="w-8 h-8 text-gray-400" />
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm border-2 border-emerald-400">
                      <UserCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">VIP Partner</p>
                      <p className="text-xs text-gray-500">role: "client"</p>
                      <p className="text-xs text-emerald-600">is_vip_partner: true</p>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Cambiar rol de "vendedor" a "cliente"
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Marcar como VIP Partner (is_vip_partner: true)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Generar código de referido único
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Migrar comisiones pendientes al nuevo sistema
                    </li>
                  </ul>
                </div>

                {/* Vendors List */}
                {preview.vendors && preview.vendors.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-4 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900">Vendedores a migrar</h3>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                      {preview.vendors.map((vendor, idx) => (
                        <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                          <div>
                            <p className="font-medium text-gray-900">{vendor.name}</p>
                            <p className="text-sm text-gray-500">{vendor.email}</p>
                          </div>
                          <div className="text-right">
                            {vendor.has_existing_code ? (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                Ya tiene código: {vendor.existing_code}
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                Se generará código
                              </span>
                            )}
                            {vendor.pending_commissions > 0 && (
                              <p className="text-xs text-amber-600 mt-1">
                                {vendor.pending_commissions} comisiones ({formatCurrency(vendor.pending_amount)})
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    onClick={fetchPreview}
                    variant="outline"
                    disabled={migrating}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar Preview
                  </Button>
                  <Button
                    onClick={executeMigration}
                    disabled={migrating || preview.total_vendors === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                  >
                    {migrating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Migrando...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Ejecutar Migración ({preview.total_vendors} vendedores)
                      </>
                    )}
                  </Button>
                </div>

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Importante</p>
                    <p className="text-sm text-amber-700">
                      Esta migración es permanente. Los vendedores podrán seguir ganando comisiones 
                      con el nuevo sistema de VIP Partners, pero ya no tendrán acceso al dashboard de vendedor anterior.
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminMigrateVendors;
